import React, { useEffect, useState } from "react";
import axios from "axios";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ApiEndpoints from "components/Api";
import ModeratorThemeProvider from "components/Moderator/ModeratorThemeProvider";
import useLoading from "components/Loading";
import DayManagementFilter from "components/DayManagementFilter";
import moment from "moment";
import ModeratorSingingPeriodSongsTable from "components/ModeratorSingingPeriodSongsTable";
import "./SingingPeriodsPage.css";

const SingingPeriodsPage = () => {
  const [periods, setPeriods] = useState([]);
  const [filteredPeriods, setFilteredPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [showSongsModal, setShowSongsModal] = useState(false);
  const [showLoading, hideLoading, Loading] = useLoading();
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(
    moment().add(1, "year").format("YYYY-MM-DD")
  );
  const [addingRow, setAddingRow] = useState(false);
  const [newStartDate, setNewStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [editingRowId, setEditingRowId] = useState(null);
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  useEffect(() => {
    fetchPeriods();
  }, []);

  const fetchPeriods = async (returnData = false) => {
    try {
      const token = localStorage.getItem("authToken");
      showLoading();
      const response = await axios.get(ApiEndpoints.MODERATOR_SINGING_PERIODS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPeriods(response.data);
      setFilteredPeriods(filterPeriods(response.data));
      hideLoading();
      if (returnData) return response.data;
    } catch (e) {
      hideLoading();
      if (returnData) return null;
    }
  };

  const filterPeriods = (data) => {
    const start = moment(startDate).startOf("day");
    const end = moment(endDate).endOf("day");
    return data.filter((p) => {
      const ps = moment(p.startDate);
      const pe = p.endDate ? moment(p.endDate) : null;
      if (pe && pe.isBefore(start)) return false;
      if (ps.isAfter(end)) return false;
      return true;
    });
  };

  const applyFilter = () => {
    setFilteredPeriods(filterPeriods(periods));
  };

  const requestSort = (key) => {
    if (sortConfig.key === key) {
      if (sortConfig.direction === "ascending") {
        setSortConfig({ key, direction: "descending" });
      } else if (sortConfig.direction === "descending") {
        setSortConfig({ key: null, direction: null });
      } else {
        setSortConfig({ key, direction: "ascending" });
      }
    } else {
      setSortConfig({ key, direction: "ascending" });
    }
  };

  const openSongsModal = (period) => {
    setSelectedPeriod(period);
    setShowSongsModal(true);
  };

  const closeSongsModal = () => {
    setShowSongsModal(false);
    setSelectedPeriod(null);
  };

  const handleSongsUpdate = async () => {
    const data = await fetchPeriods(true);
    if (data && selectedPeriod) {
      const updated = data.find(
        (p) => p.singingPeriodId === selectedPeriod.singingPeriodId
      );
      if (updated) setSelectedPeriod(updated);
    }
  };

  const addPeriod = async () => {
    if (!newStartDate && !newEndDate) return;
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        ApiEndpoints.MODERATOR_SINGING_PERIODS,
        {
          ...(newStartDate ? { startDate: newStartDate } : {}),
          ...(newEndDate ? { endDate: newEndDate } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAddingRow(false);
      fetchPeriods();
    } catch (e) {}
  };

  const startEdit = (period) => {
    setEditingRowId(period.singingPeriodId);
    setEditStartDate(moment(period.startDate).format("YYYY-MM-DD"));
    setEditEndDate(period.endDate ? moment(period.endDate).format("YYYY-MM-DD") : "");
  };

  const updatePeriod = async () => {
    if (!editingRowId) return;
    try {
      const token = localStorage.getItem("authToken");
      await axios.put(
        ApiEndpoints.MODERATOR_SINGING_PERIOD(editingRowId),
        {
          startDate: editStartDate,
          endDate: editEndDate || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingRowId(null);
      await fetchPeriods();
    } catch (e) {}
  };

  const deletePeriod = async (periodId) => {
    if (!window.confirm("Czy na pewno chcesz usunąć okres śpiewu?")) return;
    try {
      const token = localStorage.getItem("authToken");
      await axios.delete(ApiEndpoints.MODERATOR_SINGING_PERIOD(periodId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchPeriods();
    } catch (e) {}
  };

  const processedPeriods = [...filteredPeriods].sort((a, b) => {
      if (!sortConfig.key) return 0;
      const getVal = (period) => {
        if (sortConfig.key === "songsCount") {
          return period.singDaySongs ? period.singDaySongs.length : 0;
        }
        return period[sortConfig.key];
      };
      const aVal = getVal(a);
      const bVal = getVal(b);
      if (aVal < bVal) return sortConfig.direction === "ascending" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });

  return (
    <ModeratorThemeProvider>
      <div className="singing-periods-page-container">
        <Loading />
        <DayManagementFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          applyFilter={applyFilter}
        />
        <div className="singing-periods-actions">
          <Button
            color="success"
            variant="contained"
            size="small"
            onClick={() => {
              setNewStartDate("");
              setNewEndDate("");
              setAddingRow(true);
            }}
          >
            Dodaj
          </Button>
        </div>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className="sortable-header" onClick={() => requestSort("singingPeriodId")}>ID
                {sortConfig.key === "singingPeriodId" && (
                  <Icon style={{ fontSize: "0.75rem", verticalAlign: "middle" }}>
                    {sortConfig.direction === "ascending" ? "arrow_upward" : "arrow_downward"}
                  </Icon>
                )}
              </TableCell>
              <TableCell className="sortable-header" onClick={() => requestSort("startDate")}>Data rozpoczęcia
                {sortConfig.key === "startDate" && (
                  <Icon style={{ fontSize: "0.75rem", verticalAlign: "middle" }}>
                    {sortConfig.direction === "ascending" ? "arrow_upward" : "arrow_downward"}
                  </Icon>
                )}
              </TableCell>
              <TableCell className="sortable-header" onClick={() => requestSort("endDate")}>Data zakończenia
                {sortConfig.key === "endDate" && (
                  <Icon style={{ fontSize: "0.75rem", verticalAlign: "middle" }}>
                    {sortConfig.direction === "ascending" ? "arrow_upward" : "arrow_downward"}
                  </Icon>
                )}
              </TableCell>
              <TableCell className="sortable-header" onClick={() => requestSort("songsCount")}>Liczba pieśni
                {sortConfig.key === "songsCount" && (
                  <Icon style={{ fontSize: "0.75rem", verticalAlign: "middle" }}>
                    {sortConfig.direction === "ascending" ? "arrow_upward" : "arrow_downward"}
                  </Icon>
                )}
              </TableCell>
              <TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {addingRow && (
              <TableRow>
                <TableCell>Nowy</TableCell>
                <TableCell>
                  <input
                    type="date"
                    value={newStartDate}
                    onChange={(e) => setNewStartDate(e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <input
                    type="date"
                    value={newEndDate}
                    onChange={(e) => setNewEndDate(e.target.value)}
                  />
                </TableCell>
                <TableCell>-</TableCell>
                <TableCell>
                  <Button
                    color="success"
                    variant="contained"
                    size="small"
                    onClick={addPeriod}
                    style={{ marginRight: "0.25rem" }}
                  >
                    Zapisz
                  </Button>
                  <Button
                    color="success"
                    variant="outlined"
                    size="small"
                    onClick={() => setAddingRow(false)}
                  >
                    Anuluj
                  </Button>
                </TableCell>
              </TableRow>
            )}
            {processedPeriods.map((p) => (
              editingRowId === p.singingPeriodId ? (
                <TableRow key={p.singingPeriodId}>
                  <TableCell>{p.singingPeriodId}</TableCell>
                  <TableCell>
                    <input
                      type="date"
                      value={editStartDate}
                      onChange={(e) => setEditStartDate(e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <input
                      type="date"
                      value={editEndDate}
                      onChange={(e) => setEditEndDate(e.target.value)}
                    />
                  </TableCell>
                  <TableCell>{p.singDaySongs ? p.singDaySongs.length : 0}</TableCell>
                  <TableCell>
                    <Button
                      color="success"
                      variant="contained"
                      size="small"
                      onClick={updatePeriod}
                      style={{ marginRight: "0.25rem" }}
                    >
                      Zapisz
                    </Button>
                    <Button
                      color="success"
                      variant="outlined"
                      size="small"
                      onClick={() => setEditingRowId(null)}
                    >
                      Anuluj
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={p.singingPeriodId}>
                  <TableCell>{p.singingPeriodId}</TableCell>
                  <TableCell>{new Date(p.startDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {p.endDate ? new Date(p.endDate).toLocaleDateString() : "-"}
                  </TableCell>
                  <TableCell>{p.singDaySongs ? p.singDaySongs.length : 0}</TableCell>
                  <TableCell>
                    <Button
                      color="success"
                      variant="contained"
                      size="small"
                      onClick={() => openSongsModal(p)}
                      style={{ marginRight: "0.25rem" }}
                    >
                      Pieśni
                    </Button>
                    <Button
                      color="success"
                      variant="contained"
                      size="small"
                      onClick={() => startEdit(p)}
                      style={{ marginRight: "0.25rem" }}
                    >
                      Edytuj
                    </Button>
                    <Button
                      color="success"
                      variant="contained"
                      size="small"
                      onClick={() => deletePeriod(p.singingPeriodId)}
                    >
                      Usuń
                    </Button>
                  </TableCell>
                </TableRow>
              )
            ))}
          </TableBody>
        </Table>
        <Dialog
          open={showSongsModal && !!selectedPeriod}
          onClose={closeSongsModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Pieśni okresu
            {selectedPeriod
              ? ` ${new Date(selectedPeriod.startDate).toLocaleDateString()} - ${
                  selectedPeriod.endDate
                    ? new Date(selectedPeriod.endDate).toLocaleDateString()
                    : "-"}`
              : ""}
          </DialogTitle>
          <DialogContent>
            <ModeratorSingingPeriodSongsTable
              period={selectedPeriod}
              onChange={handleSongsUpdate}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeSongsModal} color="success" variant="outlined">
              Zamknij
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ModeratorThemeProvider>
  );
};

export default SingingPeriodsPage;
